import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useResolvedPath, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, Header, IconHighlight, Stepper, StepperNavFooter, useToast } from '@aimsos/ui';
import { Plus } from 'lucide-react';

import { DetailsStep, DiscardChangesModal, IdentityStep, ReviewStep } from '../components/create/index.js';
import { CREATE_STEP_IDS } from '../constants/createRecordFields.js';
import { useCreateRecordWizard } from '../hooks/useCreateRecordWizard.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useOwnerSearch } from '../hooks/useOwnerSearch.js';
import { useRecordFormOptions } from '../hooks/useRecordFormOptions.js';

const REVIEW_ID = CREATE_STEP_IDS[CREATE_STEP_IDS.length - 1];

/** Failure reason → the toast line that tells the user what to do about it. */
const FAILURE_MESSAGE_KEY = {
  forbidden: 'create.toast.forbidden',
  invalid: 'create.toast.invalid',
  error: 'create.toast.errorMessage',
};

/**
 * Create a record: Identity → Details → Review, as its own page rather than a
 * modal, so Back and a pasted link both behave and a long form has room.
 *
 * The wizard hook owns every rule; this page lays the steps out and decides
 * what to do with the outcome (toast, navigate).
 */
function CreateRecordPage() {
  const { t } = useTranslation('unified-profiles');
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { pathname: domainRoot } = useResolvedPath('..');

  const currentUser = useCurrentUser();
  const wizard = useCreateRecordWizard({ initialType: searchParams.get('type'), currentUser });
  const { options, loading: optionsLoading, error: optionsError, reload: reloadOptions } = useRecordFormOptions();
  const { owners, loading: ownersLoading, search: searchOwners } = useOwnerSearch();

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  const { entityType, values, errors, steps, stepIndex, exists, conflictMatches } = wizard;
  const isReview = steps[stepIndex].id === REVIEW_ID;
  const isFirst = stepIndex === 0;

  const stepperSteps = useMemo(
    () => steps.map((step) => ({ ...step, label: t(`create.steps.${step.id}`) })),
    [steps, t],
  );

  const phoneHandlers = useMemo(
    () => ({
      onAdd: wizard.addPhone,
      onChange: wizard.setPhoneNumber,
      onMakePrimary: wizard.setPrimaryPhone,
      onRemove: wizard.removePhone,
    }),
    [wizard.addPhone, wizard.setPhoneNumber, wizard.setPrimaryPhone, wizard.removePhone],
  );

  // The server can say "exists" after the fact (409); show those records the
  // same way the live check does.
  const existsView = useMemo(
    () => (conflictMatches.length > 0 ? { ...exists, matches: conflictMatches } : exists),
    [exists, conflictMatches],
  );

  const handleCancel = useCallback(() => {
    if (wizard.isDirty) setIsDiscardOpen(true);
    else navigate(domainRoot);
  }, [wizard.isDirty, navigate, domainRoot]);

  const handleDiscard = useCallback(() => navigate(domainRoot), [navigate, domainRoot]);
  const handleKeepEditing = useCallback(() => setIsDiscardOpen(false), []);

  const handleSubmit = useCallback(
    async (createAnother) => {
      const name = values.fullName ?? values.name ?? '';
      const outcome = await wizard.submit({ createAnother });

      if (outcome.ok) {
        toast.success({ title: t('create.toast.createdTitle'), message: t('create.toast.createdMessage', { name }) });
        if (!createAnother) navigate(`${domainRoot}/${outcome.created.entityType}/${outcome.created.id}`);
        return;
      }
      // `busy` (a second click) and `conflict` (the screen shows the match) say
      // nothing here; every other reason is a refusal the user should hear.
      if (outcome.reason === 'busy' || outcome.reason === 'conflict') return;
      toast.error({
        title: t('create.toast.errorTitle'),
        message: t(FAILURE_MESSAGE_KEY[outcome.reason] ?? FAILURE_MESSAGE_KEY.error),
      });
    },
    [wizard, values.fullName, values.name, toast, t, navigate, domainRoot],
  );

  const handleCreateAndOpen = useCallback(() => handleSubmit(false), [handleSubmit]);
  const handleCreateAndNew = useCallback(() => handleSubmit(true), [handleSubmit]);

  const secondaryAction = isReview ? (
    <Button variant="secondary" disabled={wizard.submitting} onClick={handleCreateAndNew}>
      {t('create.createAndNew')}
    </Button>
  ) : undefined;

  return (
    <div data-testid="create-record" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-6">
          <Header
            size="M"
            showIcon
            icon={<IconHighlight icon={Plus} bgColor="Primary" iconColor="PrimaryAlt" size="small" />}
            title={t(`create.titleByType.${entityType}`)}
            showDescription
            description={t('create.description')}
            showBackButton
            onBack={handleCancel}
            backButtonAriaLabel={t('create.backToRecords')}
          />

          <Stepper
            ariaLabel={t('create.stepperAriaLabel')}
            steps={stepperSteps}
            onStepClick={wizard.goToStep}
          />

          {stepIndex === 0 ? (
            <IdentityStep
              entityType={entityType}
              values={values}
              errors={errors}
              exists={existsView}
              domainRoot={domainRoot}
              onSelectType={wizard.selectType}
              onChange={wizard.setField}
              onTouch={wizard.touch}
            />
          ) : null}
          {stepIndex === 1 ? (
            <DetailsStep
              entityType={entityType}
              values={values}
              errors={errors}
              options={options}
              optionsLoading={optionsLoading}
              optionsError={optionsError}
              onRetryOptions={reloadOptions}
              owners={owners}
              ownersLoading={ownersLoading}
              onSearchOwners={searchOwners}
              onChange={wizard.setField}
              phoneHandlers={phoneHandlers}
            />
          ) : null}
          {isReview ? <ReviewStep entityType={entityType} values={values} /> : null}
        </div>
      </div>

      <StepperNavFooter
        ariaLabel={t('create.navAriaLabel')}
        backLabel={isFirst ? t('create.cancel') : t('create.back')}
        backVariant={isFirst ? 'cancel' : 'back'}
        onBack={isFirst ? handleCancel : wizard.goBack}
        primaryLabel={isReview ? t('create.createAndOpen') : t('create.next')}
        primaryIcon={isReview ? null : undefined}
        onPrimary={isReview ? handleCreateAndOpen : wizard.goNext}
        primaryDisabled={Boolean(wizard.blockReason)}
        primaryDisabledReason={wizard.blockReason ? t(`create.disabledReason.${wizard.blockReason}`) : undefined}
        primaryLoading={wizard.submitting}
        busy={wizard.submitting}
        secondaryAction={secondaryAction}
      />

      <DiscardChangesModal isOpen={isDiscardOpen} onKeepEditing={handleKeepEditing} onDiscard={handleDiscard} />
    </div>
  );
}

export default CreateRecordPage;
